import React, { useState, useEffect } from 'react';
import { API_URL, adminSessionHeaders } from '../../lib/api';

/** Normalize language labels (e.g. "English") → ISO codes (e.g. "en"). */
function normalizeLanguageCode(lang) {
  if (lang == null || lang === '') return 'en';
  const raw = String(lang).trim();
  const lower = raw.toLowerCase();
  // Chinese script variants + Cantonese keep their full codes (typed Universal Translator)
  if (['zh-hant', 'zh-tw', 'zh-hk', 'zh-mo'].includes(lower) || lower === 'traditional chinese') return 'zh-hant';
  if (['yue', 'yue-hant', 'cantonese'].includes(lower)) return 'yue';
  if (['zh-hans', 'zh-cn', 'zh-sg', 'mandarin', 'simplified chinese'].includes(lower)) return 'zh';
  if (/^[a-z]{2}([_-][a-z]{2,8})?$/i.test(raw)) {
    return raw.slice(0, 2).toLowerCase();
  }
  const nameMap = {
    english: 'en', french: 'fr', spanish: 'es', german: 'de', chinese: 'zh',
    japanese: 'ja', korean: 'ko', arabic: 'ar', hindi: 'hi', portuguese: 'pt',
    russian: 'ru', italian: 'it', dutch: 'nl', swedish: 'sv', norwegian: 'no',
    danish: 'da', finnish: 'fi', polish: 'pl', turkish: 'tr', vietnamese: 'vi',
    thai: 'th', hebrew: 'he', greek: 'el', czech: 'cs', hungarian: 'hu',
    romanian: 'ro', ukrainian: 'uk', indonesian: 'id', malay: 'ms', tagalog: 'tl',
  };
  return nameMap[lower] || raw.slice(0, 2).toLowerCase() || 'en';
}

function UserModal({ user, isOpen, onClose, onSave, isNewUser = false }) {
  const [editedUser, setEditedUser] = useState({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    telNo: '',
    country: '',
    countryCode: '',
    province: '',
    state: '',
    language: 'en',
    isActive: 1,
    accountEnabled: 1,
    mfaRequired: 1,
    mfaEnabled: 0,
    buildingRoles: []
  });
  const [complianceData, setComplianceData] = useState(null);
  const [complianceLoading, setComplianceLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [availableRoles, setAvailableRoles] = useState([]);
  const [availableBuildings, setAvailableBuildings] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  // Set after a new user is created: the modal switches to the "send invite" step.
  const [createdUser, setCreatedUser] = useState(null);
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteNotice, setInviteNotice] = useState(null); // { ok: boolean, text: string }

  // Widget permissions state
  const [widgetCatalog, setWidgetCatalog] = useState([]);
  const [selectedWidgetIds, setSelectedWidgetIds] = useState([]);
  const [widgetPermLoading, setWidgetPermLoading] = useState(false);

  /** User-created web widgets (AI Navigation URLs) — read-only for admin */
  const [userWebWidgets, setUserWebWidgets] = useState([]);
  const [userWebWidgetsLoading, setUserWebWidgetsLoading] = useState(false);
  const [userWebWidgetsError, setUserWebWidgetsError] = useState('');

  // OPTI balance (feedback_reputation) — view always; edit via step-up modal
  const [feedbackReputation, setFeedbackReputation] = useState(0);
  const [showOptiStepUpModal, setShowOptiStepUpModal] = useState(false);
  const [optiStepUpTargetUserId, setOptiStepUpTargetUserId] = useState(null);
  const [optiStepUpNewBalance, setOptiStepUpNewBalance] = useState('');
  const [optiStepUpPassword, setOptiStepUpPassword] = useState('');
  const [optiStepUpMfa, setOptiStepUpMfa] = useState('');
  const [optiStepUpReason, setOptiStepUpReason] = useState('');
  const [optiStepUpError, setOptiStepUpError] = useState('');
  const [optiStepUpLoading, setOptiStepUpLoading] = useState(false);
  const [setStartingOptiAfterCreate, setSetStartingOptiAfterCreate] = useState(false);
  const [startingOptiValue, setStartingOptiValue] = useState('0');
  const [postCreateOptiPendingClose, setPostCreateOptiPendingClose] = useState(false);

  // Building assignment state
  const [showBuildingSelector, setShowBuildingSelector] = useState(false);
  const [selectedBuildingId, setSelectedBuildingId] = useState('');
  const [buildingSearchQuery, setBuildingSearchQuery] = useState('');
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [editingBuildingId, setEditingBuildingId] = useState(null);
  const [tempRoles, setTempRoles] = useState([]);

  // BETA user communications timeline (system + admin outbound)
  const [showUserCommsModal, setShowUserCommsModal] = useState(false);
  const [userCommsLoading, setUserCommsLoading] = useState(false);
  const [userCommsError, setUserCommsError] = useState('');
  const [userCommsItems, setUserCommsItems] = useState([]);
  const [userCommsSelected, setUserCommsSelected] = useState(null);

  // Languages supported end to end (UI, OptiLaw research + premium services, Terms/Privacy).
  const languages = [
    { code: 'en', name: 'English' },
    { code: 'fr', name: 'French (Français)' },
    { code: 'yue', name: 'Cantonese (廣東話)' },
    { code: 'zh', name: 'Chinese (Simplified — Mandarin) (简体中文)' },
    { code: 'ar', name: 'Arabic (العربية)' },
  ];

  // Update form when user data changes
  useEffect(() => {
    if (user && !isNewUser) {
      // Convert existing user data to new structure
      let buildingRoles = [];
      if (user.buildingRoles && Array.isArray(user.buildingRoles)) {
        // New structure already exists — preserve names for display fallback
        buildingRoles = user.buildingRoles.map(br => ({
          buildingId: br.buildingId,
          buildingName: br.buildingName || null,
          roles: br.roles.map(r => (typeof r === 'object' ? r.id : r)),
          roleNames: br.roles.reduce((acc, r) => { if (typeof r === 'object' && r.id) acc[r.id] = r.name; return acc; }, {}),
        }));
      } else if (user.buildings && user.userTypes) {
        // Convert old structure to new
        const buildings = Array.isArray(user.buildings) ? user.buildings : [];
        const userTypes = Array.isArray(user.userTypes) ? user.userTypes : 
          (typeof user.userTypes === 'string' ? 
            (() => { 
              try { 
                const arr = JSON.parse(user.userTypes); 
                return Array.isArray(arr) ? arr : []; 
              } catch { 
                return user.userTypes.split(',').map(x => parseInt(x.trim(), 10)).filter(x => !isNaN(x)); 
              } 
            })() : []);
        
        // For legacy data, assign all roles to all buildings
        buildingRoles = buildings.map(buildingId => ({
          buildingId: buildingId,
          roles: userTypes
        }));
      }

      setEditedUser({
        id: user.id,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        username: user.username || '',
        email: user.email || '',
        telNo: user.telNo || '',
        country: user.country || '',
        countryCode: user.countryCode || '',
        province: user.province || '',
        state: user.state || '',
        language: normalizeLanguageCode(user.language || user.language_preference || 'en'),
        isActive: user.isActive !== undefined ? user.isActive : 1,
        accountEnabled: user.accountEnabled !== undefined ? user.accountEnabled : 1,
        mfaRequired: user.mfaRequired !== undefined && user.mfaRequired !== null
          ? (Number(user.mfaRequired) ? 1 : 0)
          : 1,
        mfaEnabled: user.mfaEnabled !== undefined && user.mfaEnabled !== null
          ? (Number(user.mfaEnabled) ? 1 : 0)
          : 0,
        buildingRoles: buildingRoles,
        organizationType: user.organizationType || '',
        organizationName: user.organizationName || '',
        welcomeEmailSentAt: user.welcomeEmailSentAt || null,
      });
      
      setFeedbackReputation(Number(user.feedbackReputation ?? user.feedback_reputation ?? 0));
      setSetStartingOptiAfterCreate(false);
      setStartingOptiValue('0');
      setPostCreateOptiPendingClose(false);
    } else {
      // Reset form for new user
      setEditedUser({
        firstName: '',
        lastName: '',
        username: '',
        email: '',
        telNo: '',
        country: '',
        countryCode: '',
        province: '',
        state: '',
        language: 'en',
        buildingRoles: [],
        organizationType: '',
        organizationName: ''
      });
      setFeedbackReputation(0);
      setSetStartingOptiAfterCreate(false);
      setStartingOptiValue('0');
      setPostCreateOptiPendingClose(false);
    }
    setCreatedUser(null);
    setInviteNotice(null);
  }, [user, isNewUser, isOpen]);

  useEffect(() => {
    if (isOpen) {
      setFetchingData(true);
      setError('');
      
      // Fetch roles from API
      fetch(`${API_URL}/api/roles`)
        .then(res => {
          if (!res.ok) throw new Error(`Failed to fetch roles: ${res.status}`);
          return res.json();
        })
        .then(roles => {
          setAvailableRoles(roles);
        })
        .catch(err => {
          console.error('Error fetching roles:', err);
          // Fallback to hardcoded roles
          const defaultRoles = [
            { id: 1, name: 'Landlord' },
            { id: 2, name: 'Tenant' },
            { id: 3, name: 'Valuator' },
            { id: 4, name: 'Owner' },
            { id: 5, name: 'Property Manager' },
            { id: 6, name: 'Real Estate Agent' },
            { id: 7, name: 'Service Contractor' },
            { id: 8, name: 'Leasing Agent' },
            { id: 9, name: 'Legal Compliance' },
            { id: 10, name: 'Occupant' },
            { id: 11, name: 'Building Manager' },
            { id: 12, name: 'Government Agency' },
            { id: 13, name: 'HOA Member' },
            { id: 14, name: 'Insurer' }
          ];
          setAvailableRoles(defaultRoles);
        });

      // Fetch buildings
      fetch(`${API_URL}/api/buildings`)
        .then(res => {
          if (!res.ok) throw new Error(`Failed to fetch buildings: ${res.status}`);
          return res.json();
        })
        .then(buildings => {
          // Sort buildings alphabetically
          buildings.sort((a, b) => a.name.localeCompare(b.name));
          setAvailableBuildings(buildings);
          setError('');
        })
        .catch(err => {
          console.error('Error fetching buildings:', err);
          setAvailableBuildings([]);
        })
        .finally(() => {
          setFetchingData(false);
        });

      // Fetch compliance status for existing users
      if (!isNewUser && user && user.id) {
        fetchComplianceStatus(user.id);
      }

      // Fetch widget catalog
      fetch('/api/community/widgets', { headers: { ...adminSessionHeaders() } })
        .then(r => r.ok ? r.json() : [])
        .then(data => setWidgetCatalog(Array.isArray(data) ? data : []))
        .catch(() => {});

      // Fetch user's current widget permissions
      if (!isNewUser && user && user.id) {
        setWidgetPermLoading(true);
        fetch(`/api/community/users/${user.id}/widget-permissions`, { headers: { ...adminSessionHeaders() } })
          .then(r => r.ok ? r.json() : { widgetIds: [] })
          .then(data => { setSelectedWidgetIds(data.widgetIds || []); setWidgetPermLoading(false); })
          .catch(() => setWidgetPermLoading(false));
      } else {
        // For new users, pre-select no widgets
        setSelectedWidgetIds([]);
      }
    }
  }, [isOpen, user, isNewUser]);

  useEffect(() => {
    if (!isOpen || isNewUser || !user?.id) {
      setUserWebWidgets([]);
      setUserWebWidgetsError('');
      setUserWebWidgetsLoading(false);
      return;
    }
    setUserWebWidgetsLoading(true);
    setUserWebWidgetsError('');
    fetch(`/api/community/users/${user.id}/web-widgets`, { headers: { ...adminSessionHeaders() } })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('Failed'))))
      .then((data) => {
        setUserWebWidgets(Array.isArray(data.data) ? data.data : []);
      })
      .catch(() => {
        setUserWebWidgetsError('Could not load web widgets.');
        setUserWebWidgets([]);
      })
      .finally(() => setUserWebWidgetsLoading(false));
  }, [isOpen, isNewUser, user?.id]);

  const loadUserCommunications = async () => {
    const email = (editedUser.email || user?.email || '').trim();
    const uid = editedUser.id || user?.id;
    if (!email && !uid) {
      setUserCommsError('No email on this user.');
      return;
    }
    setUserCommsLoading(true);
    setUserCommsError('');
    setUserCommsSelected(null);
    try {
      const params = new URLSearchParams();
      if (email) params.set('email', email);
      if (uid) params.set('userId', String(uid));
      const res = await fetch(`/api/communications/user-history?${params}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.error || `Failed to load communications (${res.status})`);
      }
      setUserCommsItems(Array.isArray(data.communications) ? data.communications : []);
    } catch (e) {
      setUserCommsError(e.message || 'Could not load communications.');
      setUserCommsItems([]);
    } finally {
      setUserCommsLoading(false);
    }
  };

  const openUserCommsModal = () => {
    setShowUserCommsModal(true);
    void loadUserCommunications();
  };

  const userCommsKindLabel = (kind) => {
    const map = {
      compose: 'Compose',
      engagement: 'Engagement',
      feedback_admin_reply: 'Feedback reply',
      feedback_receipt: 'Feedback receipt',
      welcome: 'Welcome',
      follow_up_connect: 'Follow-up',
      system: 'System',
    };
    return map[kind] || kind || 'Email';
  };

  const userCommsKindTone = (kind) => {
    if (kind === 'feedback_admin_reply') return { bg: 'rgba(25,239,255,0.12)', color: '#19EFFF', border: 'rgba(25,239,255,0.35)' };
    if (kind === 'feedback_receipt') return { bg: 'rgba(16,185,129,0.12)', color: '#34d399', border: 'rgba(16,185,129,0.35)' };
    if (kind === 'welcome' || kind === 'follow_up_connect' || kind === 'engagement') {
      return { bg: 'rgba(37,99,235,0.12)', color: '#60a5fa', border: 'rgba(37,99,235,0.35)' };
    }
    return { bg: 'rgba(148,163,184,0.12)', color: '#94a3b8', border: 'rgba(148,163,184,0.35)' };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditedUser(prev => ({ ...prev, [name]: value }));
  };

  /** Email a single-use set-password link. Any earlier link stops working. */
  const sendInvite = async (targetUserId) => {
    if (!targetUserId) return false;
    setInviteSending(true);
    setInviteNotice(null);
    try {
      const response = await fetch(`${API_URL}/api/community/users/${targetUserId}/send-invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...adminSessionHeaders() },
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.success) {
        setInviteNotice({ ok: false, text: `Invite not sent: ${result.error || `HTTP ${response.status}`}` });
        return false;
      }
      const expires = result.expiresAt ? new Date(result.expiresAt).toLocaleString() : 'in 72 hours';
      const pdfs = Array.isArray(result.attachments) && result.attachments.length
        ? ` Attached: ${result.attachments.join(', ')}.`
        : ' No PDF manuals were attached (check server log).';
      setInviteNotice({ ok: true, text: `Invite sent to ${result.email}. The link works once and expires ${expires}.${pdfs}` });
      setEditedUser(prev => ({ ...prev, welcomeEmailSentAt: new Date().toISOString() }));
      onSave({ __refreshOnly: true });
      return true;
    } catch (err) {
      setInviteNotice({ ok: false, text: `Invite not sent: ${err.message || err}` });
      return false;
    } finally {
      setInviteSending(false);
    }
  };

  /** Leave the post-create invite step: optional starting-OPTI step-up, otherwise close. */
  const finishCreateFlow = () => {
    const created = createdUser;
    setCreatedUser(null);
    if (created && setStartingOptiAfterCreate) {
      setPostCreateOptiPendingClose(true);
      openOptiAdjustModal(created.id, startingOptiValue);
      return;
    }
    onClose();
  };

  // Building assignment functions
  const openBuildingSelector = () => {
    console.log('openBuildingSelector called');
    console.log('Available buildings:', availableBuildings);
    console.log('Unassigned buildings:', unassignedBuildings);
    setShowBuildingSelector(true);
    setBuildingSearchQuery('');
    setSelectedBuildingId('');
    console.log('showBuildingSelector set to true');
  };

  const selectBuilding = (buildingId) => {
    const existingAssignment = editedUser.buildingRoles.find(br => br.buildingId === buildingId);
    setEditingBuildingId(buildingId);
    setTempRoles(existingAssignment ? existingAssignment.roles : []);
    setShowBuildingSelector(false);
    setShowRoleModal(true);
  };

  const editBuildingRoles = (buildingId) => {
    const existingAssignment = editedUser.buildingRoles.find(br => br.buildingId === buildingId);
    setEditingBuildingId(buildingId);
    setTempRoles(existingAssignment ? existingAssignment.roles : []);
    setShowRoleModal(true);
  };

  const removeBuildingAssignment = (buildingId) => {
    setEditedUser(prev => ({
      ...prev,
      buildingRoles: prev.buildingRoles.filter(br => br.buildingId !== buildingId)
    }));
  };

  const toggleTempRole = (roleId) => {
    setTempRoles(prev => {
      if (prev.includes(roleId)) {
        return prev.filter(r => r !== roleId);
      } else {
        return [...prev, roleId];
      }
    });
  };

  const saveBuildingRoles = () => {
    setEditedUser(prev => {
      const buildingRoles = prev.buildingRoles.filter(br => br.buildingId !== editingBuildingId);
      if (tempRoles.length > 0) {
        buildingRoles.push({
          buildingId: editingBuildingId,
          roles: tempRoles
        });
      }
      return { ...prev, buildingRoles };
    });
    setShowRoleModal(false);
    setEditingBuildingId(null);
    setTempRoles([]);
  };

  const cancelRoleModal = () => {
    setShowRoleModal(false);
    setEditingBuildingId(null);
    setTempRoles([]);
  };

  const resetOptiStepUpFields = () => {
    setOptiStepUpPassword('');
    setOptiStepUpMfa('');
    setOptiStepUpReason('');
    setOptiStepUpError('');
  };

  const openOptiAdjustModal = (forUserId, initialBalanceStr) => {
    if (!forUserId) return;
    setOptiStepUpTargetUserId(forUserId);
    setOptiStepUpNewBalance(initialBalanceStr != null ? String(initialBalanceStr) : '0');
    resetOptiStepUpFields();
    setShowOptiStepUpModal(true);
  };

  const closeOptiStepUpModal = () => {
    setShowOptiStepUpModal(false);
    setOptiStepUpTargetUserId(null);
    resetOptiStepUpFields();
    setOptiStepUpLoading(false);
    if (postCreateOptiPendingClose) {
      setPostCreateOptiPendingClose(false);
      onClose();
    }
  };

  const submitOptiStepUp = async () => {
    if (!optiStepUpTargetUserId) return;
    setOptiStepUpError('');
    setOptiStepUpLoading(true);
    const sessionToken = localStorage.getItem('sessionToken');
    try {
      const res = await fetch(`/api/community/users/${optiStepUpTargetUserId}/opti-tokens`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionToken ? { 'X-Admin-Session': sessionToken } : {}),
        },
        body: JSON.stringify({
          newBalance: parseFloat(String(optiStepUpNewBalance).replace(/,/g, '')),
          adminPassword: optiStepUpPassword,
          mfaCode: optiStepUpMfa || undefined,
          reason: optiStepUpReason || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || `Failed to update OPTI balance (${res.status})`);
      }
      const nextBal = Number(data.feedbackReputation);
      if (Number.isFinite(nextBal)) {
        setFeedbackReputation(nextBal);
        onSave({ __refreshOnly: true });
      }
      setShowOptiStepUpModal(false);
      setOptiStepUpTargetUserId(null);
      resetOptiStepUpFields();
      if (postCreateOptiPendingClose) {
        setPostCreateOptiPendingClose(false);
        onClose();
      }
    } catch (err) {
      setOptiStepUpError(err.message || 'Update failed');
    } finally {
      setOptiStepUpLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    setError('');

    // Prepare user data for submission (tel/countryCode as strings — avoids JSON number quirks)
    const userDataToSubmit = {
      ...editedUser,
      telNo: editedUser.telNo != null && editedUser.telNo !== '' ? String(editedUser.telNo).trim() : '',
      countryCode: editedUser.countryCode != null && editedUser.countryCode !== '' ? String(editedUser.countryCode).trim() : '',
      language: normalizeLanguageCode(editedUser.language || 'en'),
    };
    
    try {
      // PostgreSQL canonical path (same source as GET /api/community/users list)
      const response = await fetch(
        `/api/community/users${isNewUser ? '' : `/${user.id}`}`,
        {
          method: isNewUser ? 'POST' : 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...adminSessionHeaders(),
          },
          body: JSON.stringify(userDataToSubmit),
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const primary = errorData.error || `Failed to save user: ${response.status}`;
        const detail = errorData.details && String(errorData.details) !== String(primary) ? errorData.details : '';
        const combined = detail ? `${primary} (${detail})` : primary;

        if (errorData.error && errorData.error.includes('UNIQUE constraint failed: users.email')) {
          throw new Error('Email address already exists. Please use a different email address.');
        }
        if (errorData.error && errorData.error.includes('UNIQUE constraint failed: users.username')) {
          throw new Error('Username already exists. Please choose a different username.');
        }
        if (errorData.error && errorData.error.includes('UNIQUE constraint failed: users.telNo')) {
          throw new Error('Phone number already exists. Please use a different phone number.');
        }
        throw new Error(combined);
      }
      
      const savedUser = await response.json();

      // Save widget permissions (for both new and existing users)
      const targetUserId =
        savedUser.id || savedUser.userId || (user && user.id);
      if (targetUserId) {
        try {
          await fetch(`/api/community/users/${targetUserId}/widget-permissions`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...adminSessionHeaders() },
            body: JSON.stringify({ widgetIds: selectedWidgetIds, grantedBy: 'admin' }),
          });
        } catch (wErr) {
          console.error('Failed to save widget permissions:', wErr);
        }
      }

      const mergedForParent = {
        ...savedUser,
        id: targetUserId || savedUser.id,
        allocatedWidgets: selectedWidgetIds,
      };

      if (isNewUser && targetUserId) {
        onSave({ ...mergedForParent, __deferModalClose: true });
        setCreatedUser({
          id: targetUserId,
          email: savedUser.email || editedUser.email,
          firstName: savedUser.firstName || editedUser.firstName,
          username: savedUser.username || editedUser.username,
        });
        setInviteNotice(null);
        return;
      }

      alert('User saved successfully.');
      onSave(mergedForParent);
      onClose();
    } catch (err) {
      console.error('Save error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Helper functions
  const getBuildingName = (buildingId) => {
    const building = availableBuildings.find(b => b.id === buildingId);
    if (building) return building.name;
    // Fall back to cached name stored in editedUser.buildingRoles
    const cached = editedUser.buildingRoles.find(br => br.buildingId === buildingId);
    return (cached && cached.buildingName) ? cached.buildingName : `Building ${buildingId}`;
  };

  const getRoleName = (roleId, buildingId) => {
    const role = availableRoles.find(r => r.id === roleId);
    if (role) return role.name;
    // Fall back to cached role names stored in the building role object
    if (buildingId !== undefined) {
      const br = editedUser.buildingRoles.find(b => b.buildingId === buildingId);
      if (br && br.roleNames && br.roleNames[roleId]) return br.roleNames[roleId];
    }
    // Try any building's cached names
    for (const br of editedUser.buildingRoles) {
      if (br.roleNames && br.roleNames[roleId]) return br.roleNames[roleId];
    }
    return `Role ${roleId}`;
  };

  // Fetch and display compliance status from the real audit API
  const fetchComplianceStatus = async (userId) => {
    setComplianceLoading(true);
    try {
      const res = await fetch(`/api/community/users/${userId}/compliance`, {
        headers: { ...adminSessionHeaders() },
      });
      if (!res.ok) throw new Error('Failed to fetch compliance');
      const data = await res.json();
      setComplianceData(data);
      setTimeout(() => updateComplianceDisplay({
        termsAccepted: data.termsAccepted,
        betaDisclaimerAccepted: data.betaDisclaimerAccepted,
        termsAcceptedAt: data.termsAcceptedAt,
        betaDisclaimerAcceptedAt: data.betaDisclaimerAcceptedAt,
      }), 100);
    } catch (error) {
      console.error('Error fetching compliance status:', error);
      setComplianceData(null);
      setTimeout(() => updateComplianceDisplay({
        termsAccepted: false,
        betaDisclaimerAccepted: false,
        termsAcceptedAt: null,
        betaDisclaimerAcceptedAt: null,
      }), 100);
    } finally {
      setComplianceLoading(false);
    }
  };

  // Update compliance display indicators
  const updateComplianceDisplay = (data) => {
    const termsIndicator = document.getElementById('termsStatusIndicator');
    const termsText = document.getElementById('termsStatusText');
    const betaIndicator = document.getElementById('betaStatusIndicator');
    const betaText = document.getElementById('betaStatusText');
    const overallIndicator = document.getElementById('overallComplianceIndicator');
    const overallText = document.getElementById('overallComplianceText');

    // If elements aren't ready yet, retry after a short delay
    if (!termsIndicator || !termsText || !betaIndicator || !betaText || !overallIndicator || !overallText) {
      setTimeout(() => updateComplianceDisplay(data), 200);
      return;
    }

    const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : null;

    if (termsIndicator && termsText) {
      if (data.termsAccepted) {
        termsIndicator.style.backgroundColor = '#059669';
        termsIndicator.style.borderColor = '#047857';
        termsIndicator.innerHTML = '✅';
        const dateStr = fmtDate(data.termsAcceptedAt);
        termsText.textContent = dateStr ? `Accepted ${dateStr}` : 'Accepted';
      } else {
        termsIndicator.style.backgroundColor = '#dc2626';
        termsIndicator.style.borderColor = '#991b1b';
        termsIndicator.innerHTML = '❌';
        termsText.textContent = 'Not Accepted';
      }
    }

    if (betaIndicator && betaText) {
      if (data.betaDisclaimerAccepted) {
        betaIndicator.style.backgroundColor = '#059669';
        betaIndicator.style.borderColor = '#047857';
        betaIndicator.innerHTML = '✅';
        const dateStr = fmtDate(data.betaDisclaimerAcceptedAt);
        betaText.textContent = dateStr ? `Accepted ${dateStr}` : 'Accepted';
      } else {
        betaIndicator.style.backgroundColor = '#dc2626';
        betaIndicator.style.borderColor = '#991b1b';
        betaIndicator.innerHTML = '❌';
        betaText.textContent = 'Not Accepted';
      }
    }

    if (overallIndicator && overallText) {
      const isCompliant = data.termsAccepted && data.betaDisclaimerAccepted;
      if (isCompliant) {
        overallIndicator.style.backgroundColor = '#059669';
        overallIndicator.style.borderColor = '#047857';
        overallIndicator.innerHTML = '✅';
        overallText.textContent = 'Compliant';
      } else {
        overallIndicator.style.backgroundColor = '#dc2626';
        overallIndicator.style.borderColor = '#991b1b';
        overallIndicator.innerHTML = '❌';
        overallText.textContent = 'Non-Compliant';
      }
    }
  };

  const filteredBuildings = availableBuildings.filter(building =>
    building.name.toLowerCase().includes(buildingSearchQuery.toLowerCase()) ||
    building.address.toLowerCase().includes(buildingSearchQuery.toLowerCase())
  );

  const unassignedBuildings = availableBuildings.filter(building =>
    !editedUser.buildingRoles.some(br => br.buildingId === building.id)
  );

  const renderInviteNotice = () => (inviteNotice ? (
    <div style={{
      marginTop: 12,
      padding: '10px 12px',
      borderRadius: 6,
      fontSize: 13,
      lineHeight: 1.5,
      background: inviteNotice.ok ? 'rgba(16,185,129,0.12)' : 'rgba(220,38,38,0.15)',
      border: `1px solid ${inviteNotice.ok ? 'rgba(16,185,129,0.45)' : '#dc2626'}`,
      color: inviteNotice.ok ? '#d1fae5' : '#fecaca',
    }}>
      {inviteNotice.text}
    </div>
  ) : null);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-overlay">
        <div className="modal-content user-modal">
          <div className="modal-header">
            <h2>{createdUser ? 'User created' : (isNewUser ? 'Add New User' : 'Edit User')}</h2>
            <button className="close-button" onClick={createdUser ? finishCreateFlow : onClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>
          
          {error && (
            <div
              className="error-message"
              style={{
                margin: '12px 16px 0',
                padding: '12px 14px',
                background: 'rgba(220, 38, 38, 0.15)',
                border: '1px solid #dc2626',
                borderRadius: 8,
                color: '#fecaca',
                fontWeight: 600,
              }}
            >
              {error}
            </div>
          )}
          {fetchingData && <div className="loading-spinner">Loading data...</div>}

          {createdUser ? (
            <>
              <div className="modal-body">
                <div style={{ padding: '14px 16px', borderRadius: 8, background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.45)', color: '#d1fae5', fontWeight: 600 }}>
                  ✅ Account created for {createdUser.firstName || createdUser.username} ({createdUser.username}).
                </div>
                <p style={{ margin: '16px 0 0', fontSize: 14, color: 'var(--text-primary)', lineHeight: 1.6 }}>
                  Send the invite now? <code>{createdUser.email}</code> gets a link to choose their own password
                  (it works once and expires in 72 hours) plus the PDF manuals. Nobody else ever sees the password.
                </p>
                {!inviteNotice?.ok && (
                  <p style={{ margin: '10px 0 0', fontSize: 12, color: 'var(--text-secondary)' }}>
                    Not ready? Choose Not now and send it later from Edit User.
                  </p>
                )}
                {renderInviteNotice()}
              </div>
              <div className="modal-footer">
                {inviteNotice?.ok ? (
                  <button type="button" className="btn btn-primary" onClick={finishCreateFlow}>
                    {setStartingOptiAfterCreate ? 'Next: starting OPTI' : 'Done'}
                  </button>
                ) : (
                  <>
                    <button type="button" className="btn btn-secondary" onClick={finishCreateFlow} disabled={inviteSending}>
                      Not now
                    </button>
                    <button type="button" className="btn btn-primary" onClick={() => sendInvite(createdUser.id)} disabled={inviteSending}>
                      {inviteSending ? 'Sending…' : (inviteNotice ? 'Try again' : 'Send invite email')}
                    </button>
                  </>
                )}
              </div>
            </>
          ) : (
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {!fetchingData && (
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="firstName">First Name</label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={editedUser.firstName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="lastName">Last Name</label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={editedUser.lastName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="username">Username</label>
                    <input
                      type="text"
                      id="username"
                      name="username"
                      value={editedUser.username}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={editedUser.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="telNo">Telephone</label>
                    <input
                      type="tel"
                      id="telNo"
                      name="telNo"
                      value={editedUser.telNo}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="country">Country</label>
                    <select
                      id="country"
                      name="country"
                      value={editedUser.country}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Country</option>
                      <option value="Canada">Canada</option>
                      <option value="USA">USA</option>
                      <option value="China">China</option>
                      <option value="South Africa">South Africa</option>
                      <option disabled>──────────</option>
                      {[
                        "Afghanistan","Albania","Algeria","Andorra","Angola","Antigua and Barbuda","Argentina","Armenia","Australia","Austria","Azerbaijan","Bahamas","Bahrain","Bangladesh","Barbados","Belarus","Belgium","Belize","Benin","Bhutan","Bolivia","Bosnia and Herzegovina","Botswana","Brazil","Brunei","Bulgaria","Burkina Faso","Burundi","Cabo Verde","Cambodia","Cameroon","Central African Republic","Chad","Chile","Colombia","Comoros","Congo (Congo-Brazzaville)","Costa Rica","Croatia","Cuba","Cyprus","Czech Republic","Democratic Republic of the Congo","Denmark","Djibouti","Dominica","Dominican Republic","Ecuador","Egypt","El Salvador","Equatorial Guinea","Eritrea","Estonia","Eswatini (fmr. Swaziland)","Ethiopia","Fiji","Finland","France","Gabon","Gambia","Georgia","Germany","Ghana","Greece","Grenada","Guatemala","Guinea","Guinea-Bissau","Guyana","Haiti","Honduras","Hungary","Iceland","India","Indonesia","Iran","Iraq","Ireland","Israel","Italy","Jamaica","Japan","Jordan","Kazakhstan","Kenya","Kiribati","Kuwait","Kyrgyzstan","Laos","Latvia","Lebanon","Lesotho","Liberia","Libya","Liechtenstein","Lithuania","Luxembourg","Madagascar","Malawi","Malaysia","Maldives","Mali","Malta","Marshall Islands","Mauritania","Mauritius","Mexico","Micronesia","Moldova","Monaco","Mongolia","Montenegro","Morocco","Mozambique","Myanmar (formerly Burma)","Namibia","Nauru","Nepal","Netherlands","New Zealand","Nicaragua","Niger","Nigeria","North Korea","North Macedonia","Norway","Oman","Pakistan","Palau","Palestine State","Panama","Papua New Guinea","Paraguay","Peru","Philippines","Poland","Portugal","Qatar","Romania","Russia","Rwanda","Saint Kitts and Nevis","Saint Lucia","Saint Vincent and the Grenadines","Samoa","San Marino","Sao Tome and Principe","Saudi Arabia","Senegal","Serbia","Seychelles","Sierra Leone","Singapore","Slovakia","Slovenia","Solomon Islands","Somalia","South Korea","South Sudan","Spain","Sri Lanka","Sudan","Suriname","Sweden","Switzerland","Syria","Taiwan","Tajikistan","Tanzania","Thailand","Timor-Leste","Togo","Tonga","Trinidad and Tobago","Tunisia","Turkey","Turkmenistan","Tuvalu","Uganda","Ukraine","United Arab Emirates","United Kingdom","Uruguay","Uzbekistan","Vanuatu","Vatican City","Venezuela","Vietnam","Yemen","Zambia","Zimbabwe"
                      ].filter(country => !["Canada","USA","China","South Africa"].includes(country)).map(country => (
                        <option key={country} value={country}>{country}</option>
                      ))}
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="countryCode">Country Code</label>
                    <input
                      type="text"
                      id="countryCode"
                      name="countryCode"
                      value={editedUser.countryCode}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  {/* Province/State Dropdown for Top Countries */}
                  {(() => {
                    const country = editedUser.country;
                    if (country === 'Canada') {
                      return (
                        <div className="form-group">
                          <label htmlFor="province">Province</label>
                          <select
                            id="province"
                            name="province"
                            value={editedUser.province || ''}
                            onChange={handleChange}
                            required
                          >
                            <option value="">Select Province</option>
                            <option value="Alberta">Alberta</option>
                            <option value="British Columbia">British Columbia</option>
                            <option value="Manitoba">Manitoba</option>
                            <option value="New Brunswick">New Brunswick</option>
                            <option value="Newfoundland and Labrador">Newfoundland and Labrador</option>
                            <option value="Nova Scotia">Nova Scotia</option>
                            <option value="Ontario">Ontario</option>
                            <option value="Prince Edward Island">Prince Edward Island</option>
                            <option value="Quebec">Quebec</option>
                            <option value="Saskatchewan">Saskatchewan</option>
                            <option value="Northwest Territories">Northwest Territories</option>
                            <option value="Nunavut">Nunavut</option>
                            <option value="Yukon">Yukon</option>
                          </select>
                        </div>
                      );
                    } else if (country === 'USA') {
                      return (
                        <div className="form-group">
                          <label htmlFor="state">State</label>
                          <select
                            id="state"
                            name="state"
                            value={editedUser.state || ''}
                            onChange={handleChange}
                            required
                          >
                            <option value="">Select State</option>
                            <option value="Alabama">Alabama</option>
                            <option value="Alaska">Alaska</option>
                            <option value="Arizona">Arizona</option>
                            <option value="Arkansas">Arkansas</option>
                            <option value="California">California</option>
                            <option value="Colorado">Colorado</option>
                            <option value="Connecticut">Connecticut</option>
                            <option value="Delaware">Delaware</option>
                            <option value="Florida">Florida</option>
                            <option value="Georgia">Georgia</option>
                            <option value="Hawaii">Hawaii</option>
                            <option value="Idaho">Idaho</option>
                            <option value="Illinois">Illinois</option>
                            <option value="Indiana">Indiana</option>
                            <option value="Iowa">Iowa</option>
                            <option value="Kansas">Kansas</option>
                            <option value="Kentucky">Kentucky</option>
                            <option value="Louisiana">Louisiana</option>
                            <option value="Maine">Maine</option>
                            <option value="Maryland">Maryland</option>
                            <option value="Massachusetts">Massachusetts</option>
                            <option value="Michigan">Michigan</option>
                            <option value="Minnesota">Minnesota</option>
                            <option value="Mississippi">Mississippi</option>
                            <option value="Missouri">Missouri</option>
                            <option value="Montana">Montana</option>
                            <option value="Nebraska">Nebraska</option>
                            <option value="Nevada">Nevada</option>
                            <option value="New Hampshire">New Hampshire</option>
                            <option value="New Jersey">New Jersey</option>
                            <option value="New Mexico">New Mexico</option>
                            <option value="New York">New York</option>
                            <option value="North Carolina">North Carolina</option>
                            <option value="North Dakota">North Dakota</option>
                            <option value="Ohio">Ohio</option>
                            <option value="Oklahoma">Oklahoma</option>
                            <option value="Oregon">Oregon</option>
                            <option value="Pennsylvania">Pennsylvania</option>
                            <option value="Rhode Island">Rhode Island</option>
                            <option value="South Carolina">South Carolina</option>
                            <option value="South Dakota">South Dakota</option>
                            <option value="Tennessee">Tennessee</option>
                            <option value="Texas">Texas</option>
                            <option value="Utah">Utah</option>
                            <option value="Vermont">Vermont</option>
                            <option value="Virginia">Virginia</option>
                            <option value="Washington">Washington</option>
                            <option value="West Virginia">West Virginia</option>
                            <option value="Wisconsin">Wisconsin</option>
                            <option value="Wyoming">Wyoming</option>
                          </select>
                        </div>
                      );
                    } else if (country === 'China') {
                      return (
                        <div className="form-group">
                          <label htmlFor="province">Province</label>
                          <select
                            id="province"
                            name="province"
                            value={editedUser.province || ''}
                            onChange={handleChange}
                            required
                          >
                            <option value="">Select Province</option>
                            <option value="Anhui">Anhui</option>
                            <option value="Beijing">Beijing</option>
                            <option value="Chongqing">Chongqing</option>
                            <option value="Fujian">Fujian</option>
                            <option value="Gansu">Gansu</option>
                            <option value="Guangdong">Guangdong</option>
                            <option value="Guangxi">Guangxi</option>
                            <option value="Guizhou">Guizhou</option>
                            <option value="Hainan">Hainan</option>
                            <option value="Hebei">Hebei</option>
                            <option value="Heilongjiang">Heilongjiang</option>
                            <option value="Henan">Henan</option>
                            <option value="Hong Kong">Hong Kong</option>
                            <option value="Hubei">Hubei</option>
                            <option value="Hunan">Hunan</option>
                            <option value="Inner Mongolia">Inner Mongolia</option>
                            <option value="Jiangsu">Jiangsu</option>
                            <option value="Jiangxi">Jiangxi</option>
                            <option value="Jilin">Jilin</option>
                            <option value="Liaoning">Liaoning</option>
                            <option value="Macau">Macau</option>
                            <option value="Ningxia">Ningxia</option>
                            <option value="Qinghai">Qinghai</option>
                            <option value="Shaanxi">Shaanxi</option>
                            <option value="Shandong">Shandong</option>
                            <option value="Shanghai">Shanghai</option>
                            <option value="Shanxi">Shanxi</option>
                            <option value="Sichuan">Sichuan</option>
                            <option value="Tianjin">Tianjin</option>
                            <option value="Tibet">Tibet</option>
                            <option value="Xinjiang">Xinjiang</option>
                            <option value="Yunnan">Yunnan</option>
                            <option value="Zhejiang">Zhejiang</option>
                          </select>
                        </div>
                      );
                    } else if (country === 'South Africa') {
                      return (
                        <div className="form-group">
                          <label htmlFor="province">Province</label>
                          <select
                            id="province"
                            name="province"
                            value={editedUser.province || ''}
                            onChange={handleChange}
                            required
                          >
                            <option value="">Select Province</option>
                            <option value="Eastern Cape">Eastern Cape</option>
                            <option value="Free State">Free State</option>
                            <option value="Gauteng">Gauteng</option>
                            <option value="KwaZulu-Natal">KwaZulu-Natal</option>
                            <option value="Limpopo">Limpopo</option>
                            <option value="Mpumalanga">Mpumalanga</option>
                            <option value="North West">North West</option>
                            <option value="Northern Cape">Northern Cape</option>
                            <option value="Western Cape">Western Cape</option>
                          </select>
                        </div>
                      );
                    }
                    return null;
                  })()}
                  <div className="form-group">
                    <label htmlFor="language">Language Preference</label>
                    <select
                      id="language"
                      name="language"
                      value={editedUser.language}
                      onChange={handleChange}
                      required
                    >
                      {languages.map(lang => (
                        <option key={lang.code} value={lang.code}>
                          {lang.name}
                        </option>
                      ))}
                      {editedUser.language && !languages.some(l => l.code === editedUser.language) && (
                        <option value={editedUser.language}>
                          {editedUser.language} (unsupported — choose one above)
                        </option>
                      )}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="accountEnabled">Account Status</label>
                    <select
                      id="accountEnabled"
                      name="accountEnabled"
                      value={editedUser.accountEnabled}
                      onChange={(e) => handleChange({ target: { name: 'accountEnabled', value: parseInt(e.target.value) } })}
                      style={{
                        backgroundColor: editedUser.accountEnabled ? '#d4edda' : '#f8d7da',
                        color: editedUser.accountEnabled ? '#155724' : '#721c24',
                        fontWeight: 'bold'
                      }}
                    >
                      <option value={1}>🟢 Enabled</option>
                      <option value={0}>🔴 Disabled</option>
                    </select>
                  </div>

                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label htmlFor="mfaRequired">
                      Two-Factor Authentication (2FA)
                    </label>
                    <select
                      id="mfaRequired"
                      name="mfaRequired"
                      value={editedUser.mfaRequired}
                      onChange={(e) => handleChange({
                        target: { name: 'mfaRequired', value: parseInt(e.target.value, 10) },
                      })}
                      style={{
                        backgroundColor: editedUser.mfaRequired ? '#d4edda' : '#fff3cd',
                        color: editedUser.mfaRequired ? '#155724' : '#856404',
                        fontWeight: 'bold',
                      }}
                    >
                      <option value={1}>🔒 Required — password + Google Authenticator</option>
                      <option value={0}>🔓 Off — password only (no authenticator)</option>
                    </select>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6, lineHeight: 1.4 }}>
                      Controls login at <strong>realty.optiprime.ai</strong> / Optimum Admin.
                      Turn <strong>Off</strong> for users who cannot use an authenticator app (e.g. Michelle).
                      {Number(editedUser.mfaEnabled) === 1 ? (
                        <span style={{ display: 'block', marginTop: 4 }}>
                          Authenticator status: <strong style={{ color: '#155724' }}>enrolled</strong>
                          {!editedUser.mfaRequired ? ' (will not be asked while 2FA is Off)' : ''}
                        </span>
                      ) : (
                        <span style={{ display: 'block', marginTop: 4 }}>
                          Authenticator status: <strong>not enrolled</strong>
                        </span>
                      )}
                    </div>
                  </div>

                  {/* OPTI token balance (users.feedback_reputation) */}
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label style={{ fontSize: '16px', fontWeight: 'bold', color: 'var(--text-primary)', display: 'block', marginBottom: '8px' }}>
                      <i className="fas fa-coins" style={{ marginRight: '8px', color: 'var(--primary-color)' }}></i>
                      OPTI token balance
                    </label>
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '14px 16px',
                        backgroundColor: 'var(--surface-color)',
                        borderRadius: '8px',
                        border: '1px solid var(--border-color)',
                      }}
                    >
                      <div style={{ flex: '1 1 200px' }}>
                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                          Current balance (earned via OPTI system; default 0 for new users)
                        </div>
                        <div style={{ fontSize: '22px', fontWeight: '700', color: 'var(--text-primary)' }}>
                          {isNewUser && !editedUser.id
                            ? '—'
                            : Number(feedbackReputation).toLocaleString(undefined, {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 4,
                              })}{' '}
                          <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text-secondary)' }}>OPTI</span>
                        </div>
                      </div>
                      {!isNewUser && (editedUser.id || user?.id) ? (
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={() =>
                            openOptiAdjustModal(editedUser.id || user.id, feedbackReputation)
                          }
                        >
                          Adjust balance…
                        </button>
                      ) : null}
                    </div>
                    {isNewUser ? (
                      <div style={{ marginTop: '12px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                          <input
                            type="checkbox"
                            checked={setStartingOptiAfterCreate}
                            onChange={(e) => setSetStartingOptiAfterCreate(e.target.checked)}
                          />
                          After this user is saved, prompt to set starting OPTI (admin password + authenticator)
                        </label>
                        {setStartingOptiAfterCreate ? (
                          <div style={{ marginTop: '8px', maxWidth: '240px' }}>
                            <label htmlFor="startingOptiValue" style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                              Starting balance to apply
                            </label>
                            <input
                              id="startingOptiValue"
                              type="number"
                              min="0"
                              step="0.0001"
                              value={startingOptiValue}
                              onChange={(e) => setStartingOptiValue(e.target.value)}
                              style={{ width: '100%', marginTop: '4px' }}
                            />
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>

                  {/* Compliance Status Section */}
                  <div className="form-group compliance-section" style={{ gridColumn: '1 / -1' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <label style={{ fontSize: '16px', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                        <i className="fas fa-shield-alt" style={{ marginRight: '8px', color: 'var(--primary-color)' }}></i>
                        OPTI Token Compliance Status
                      </label>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          type="button"
                          className="btn btn-success btn-sm"
                          onClick={() => fetchComplianceStatus(user.id)}
                          title="Refresh compliance status from database"
                          disabled={complianceLoading}
                        >
                          <i className={`fas ${complianceLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`}></i> {complianceLoading ? 'Loading…' : 'Refresh'}
                        </button>
                      </div>
                    </div>
                    
                    <div style={{ 
                      display: 'flex', 
                      gap: '16px',
                      padding: '16px',
                      backgroundColor: 'var(--surface-color)',
                      borderRadius: '8px',
                      border: '1px solid var(--border-color)'
                    }}>
                      {/* Terms Acceptance Status */}
                      <div style={{ flex: 1, textAlign: 'center' }}>
                        <div style={{ 
                          width: '60px', 
                          height: '60px', 
                          borderRadius: '50%', 
                          margin: '0 auto 8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '24px',
                          fontWeight: 'bold',
                          color: 'white',
                          backgroundColor: '#dc2626', // Default red
                          border: '3px solid #991b1b'
                        }} id="termsStatusIndicator">
                          ❌
                        </div>
                        <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '4px' }}>Terms Acceptance</div>
                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }} id="termsStatusText">Loading...</div>
                      </div>
                      
                      {/* BETA Disclaimer Status */}
                      <div style={{ flex: 1, textAlign: 'center' }}>
                        <div style={{ 
                          width: '60px', 
                          height: '60px', 
                          borderRadius: '50%', 
                          margin: '0 auto 8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '24px',
                          fontWeight: 'bold',
                          color: 'white',
                          backgroundColor: '#dc2626', // Default red
                          border: '3px solid #991b1b'
                        }} id="betaStatusIndicator">
                          ❌
                        </div>
                        <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '4px' }}>BETA Disclaimer</div>
                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }} id="betaStatusText">Loading...</div>
                      </div>
                      
                      {/* Overall Compliance */}
                      <div style={{ flex: 1, textAlign: 'center' }}>
                        <div style={{ 
                          width: '60px', 
                          height: '60px', 
                          borderRadius: '50%', 
                          margin: '0 auto 8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '24px',
                          fontWeight: 'bold',
                          color: 'white',
                          backgroundColor: '#dc2626', // Default red
                          border: '3px solid #991b1b'
                        }} id="overallComplianceIndicator">
                          ❌
                        </div>
                        <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '4px' }}>Overall Status</div>
                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }} id="overallComplianceText">Loading...</div>
                      </div>
                    </div>
                    
                    {/* Audit log of acceptance events */}
                    {complianceData && complianceData.auditLog && complianceData.auditLog.length > 0 && (
                      <div style={{ marginTop: '12px' }}>
                        <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          <i className="fas fa-history" style={{ marginRight: '6px' }}></i>Acceptance Audit Log
                        </div>
                        <div style={{ maxHeight: '160px', overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: '6px', backgroundColor: 'var(--surface-color)' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                            <thead>
                              <tr style={{ backgroundColor: 'rgba(0,0,0,0.2)' }}>
                                <th style={{ padding: '6px 10px', textAlign: 'left', color: 'var(--text-secondary)' }}>Event</th>
                                <th style={{ padding: '6px 10px', textAlign: 'left', color: 'var(--text-secondary)' }}>Status</th>
                                <th style={{ padding: '6px 10px', textAlign: 'left', color: 'var(--text-secondary)' }}>Source</th>
                                <th style={{ padding: '6px 10px', textAlign: 'left', color: 'var(--text-secondary)' }}>Date</th>
                              </tr>
                            </thead>
                            <tbody>
                              {complianceData.auditLog.map((entry, idx) => (
                                <tr key={entry.id} style={{ borderTop: idx > 0 ? '1px solid var(--border-color)' : 'none' }}>
                                  <td style={{ padding: '5px 10px', color: 'var(--text-primary)' }}>
                                    {entry.event_type === 'terms_accepted' ? 'Terms of Service' : 'BETA Disclaimer'}
                                  </td>
                                  <td style={{ padding: '5px 10px' }}>
                                    <span style={{ color: entry.accepted ? '#059669' : '#dc2626', fontWeight: '600' }}>
                                      {entry.accepted ? '✅ Accepted' : '❌ Declined'}
                                    </span>
                                  </td>
                                  <td style={{ padding: '5px 10px', color: 'var(--text-secondary)' }}>{entry.source || '—'}</td>
                                  <td style={{ padding: '5px 10px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                                    {new Date(entry.created_at).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Note about account control */}
                    <div style={{ 
                      textAlign: 'center',
                      padding: '12px',
                      backgroundColor: 'var(--surface-color)',
                      borderRadius: '6px',
                      border: '1px solid var(--border-color)',
                      marginTop: '12px',
                      fontSize: '13px',
                      color: 'var(--text-secondary)'
                    }}>
                      <i className="fas fa-info-circle" style={{ marginRight: '6px' }}></i>
                      Use the "Account Status" dropdown above to enable/disable user accounts
                    </div>
                  </div>

                  
                  {/* New Scalable Building Assignment Section */}
                  <div className="form-group building-assignments-section" style={{ gridColumn: '1 / -1' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <label>Building Role Assignments</label>
                      <button 
                        type="button" 
                        className="btn btn-primary btn-sm"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('Add Building button clicked');
                          console.log('Button disabled?', unassignedBuildings.length === 0);
                          openBuildingSelector();
                        }}
                        disabled={unassignedBuildings.length === 0}
                      >
                        <i className="fas fa-plus"></i> Add Building
                      </button>
                    </div>
                    
                    {editedUser.buildingRoles.length > 0 ? (
                      <div className="current-assignments" style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: '12px',
                        maxHeight: '300px',
                        overflowY: 'auto',
                        border: '1px solid var(--border-color)',
                        borderRadius: '8px',
                        padding: '16px'
                      }}>
                        {editedUser.buildingRoles.map((assignment, index) => (
                          <div key={assignment.buildingId} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '12px',
                            backgroundColor: 'var(--surface-color)',
                            borderRadius: '6px',
                            border: '1px solid var(--border-color)'
                          }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                {getBuildingName(assignment.buildingId)}
                              </div>
                              <div style={{ fontSize: '0.9em', color: 'var(--text-secondary)' }}>
                                Roles: {assignment.roles.map(roleId => getRoleName(roleId, assignment.buildingId)).join(', ')}
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  editBuildingRoles(assignment.buildingId);
                                }}
                                title="Edit roles"
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                              <button
                                type="button"
                                className="btn btn-danger btn-sm"
                                onClick={() => {
                                  if (window.confirm(`Are you sure you want to remove the building assignment for ${getBuildingName(assignment.buildingId)}? This action cannot be undone.`)) {
                                    removeBuildingAssignment(assignment.buildingId);
                                  }
                                }}
                                title="Remove assignment"
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{
                        textAlign: 'center',
                        padding: '32px',
                        color: 'var(--text-secondary)',
                        backgroundColor: 'var(--surface-color)',
                        borderRadius: '8px',
                        border: '1px solid var(--border-color)'
                      }}>
                        No building assignments yet. Click "Add Building" to assign roles.
                      </div>
                    )}
                  </div>
                  {/* User Type Dropdown */}
                  <div className="form-group">
                    <label htmlFor="organizationType">User Type</label>
                    <select
                      id="organizationType"
                      name="organizationType"
                      value={editedUser.organizationType || ''}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Type</option>
                      <option value="Master Admin">Master Admin</option>
                      <option value="Admin">Admin</option>
                      <option value="BETA User">BETA User</option>
                      <option value="Enterprise User">Enterprise User</option>
                    </select>
                  </div>
                  {/* Organization Name Text Field */}
                  <div className="form-group">
                    <label htmlFor="organizationName">Organization Name</label>
                    <input
                      type="text"
                      id="organizationName"
                      name="organizationName"
                      value={editedUser.organizationName || ''}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Widget Permissions Section */}
            <div style={{ margin: '20px 0', padding: '20px', background: 'var(--card-bg, #1e293b)', border: '1px solid var(--border-color, #334155)', borderRadius: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>
                  <i className="fas fa-th-large" style={{ marginRight: 8, color: 'var(--accent, #2563eb)' }}></i>Widget Permissions
                </h3>
                {widgetPermLoading && <i className="fas fa-spinner fa-spin" style={{ color: 'var(--accent)' }}></i>}
              </div>
              {widgetCatalog.length === 0 ? (
                <p style={{ color: 'var(--text-secondary)', fontSize: 13, margin: 0 }}>Loading widget catalog…</p>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
                  {widgetCatalog.map(w => {
                    const checked = selectedWidgetIds.includes(w.id);
                    return (
                      <label
                        key={w.id}
                        style={{
                          display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
                          padding: '10px 12px', borderRadius: 8,
                          background: checked ? 'rgba(37,99,235,0.12)' : 'var(--input-bg, #0f172a)',
                          border: `1px solid ${checked ? 'var(--accent, #2563eb)' : 'var(--border-color, #334155)'}`,
                          transition: 'all 0.15s',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            setSelectedWidgetIds(prev =>
                              prev.includes(w.id) ? prev.filter(x => x !== w.id) : [...prev, w.id]
                            );
                          }}
                          style={{ marginTop: 2, accentColor: 'var(--accent, #2563eb)' }}
                        />
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>
                            <i className={`fas ${w.iconName || 'fa-puzzle-piece'}`} style={{ marginRight: 6, color: 'var(--accent, #2563eb)' }}></i>
                            {w.name}
                          </div>
                          {w.status === 'coming_soon' && (
                            <span style={{ fontSize: 10, color: '#d97706', fontWeight: 600 }}>Coming soon</span>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
              <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 12, marginBottom: 0 }}>
                Selected widgets will appear on the user's dashboard. Unselected widgets will be hidden.
              </p>
            </div>

            {/* Communications timeline — system + admin outbound */}
            {!isNewUser && (
              <div style={{ margin: '20px 0', padding: '20px', background: 'var(--card-bg, #1e293b)', border: '1px solid var(--border-color, #334155)', borderRadius: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>
                      <i className="fas fa-envelope" style={{ marginRight: 8, color: 'var(--accent, #19EFFF)' }}></i>
                      Communications
                    </h3>
                    <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '8px 0 0' }}>
                      Outbound emails to this BETA user from the system and admin (welcome, follow-ups, feedback replies, Compose).
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={openUserCommsModal}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    <i className="fas fa-inbox" style={{ marginRight: 8 }}></i>
                    View communications…
                  </button>
                </div>
              </div>
            )}

            {/* User-created Web Widgets (AI Navigation) — read-only */}
            {!isNewUser && (
              <div style={{ margin: '20px 0', padding: '20px', background: 'var(--card-bg, #1e293b)', border: '1px solid var(--border-color, #334155)', borderRadius: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                  <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>
                    <i className="fas fa-globe" style={{ marginRight: 8, color: 'var(--accent, #2563eb)' }}></i>
                    Web Widgets (user-created)
                  </h3>
                  {userWebWidgetsLoading && <i className="fas fa-spinner fa-spin" style={{ color: 'var(--accent)' }}></i>}
                </div>
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 12px' }}>
                  URLs the user added in <strong>AI Navigation</strong> on My Dashboard. Read-only for support and review.
                </p>
                {userWebWidgetsError ? (
                  <p style={{ color: '#f87171', fontSize: 13, margin: 0 }}>{userWebWidgetsError}</p>
                ) : userWebWidgets.length === 0 && !userWebWidgetsLoading ? (
                  <p style={{ color: 'var(--text-secondary)', fontSize: 13, margin: 0, fontStyle: 'italic' }}>
                    No web widgets yet — the user has not added any custom URLs.
                  </p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {userWebWidgets.map((ww) => {
                      const title = (ww.user_title && ww.user_title.trim()) || (ww.page_title && ww.page_title.trim()) || 'Untitled';
                      const status = (ww.status || '').toLowerCase();
                      const statusStyle =
                        status === 'ready'
                          ? { bg: 'rgba(34,197,94,0.15)', color: '#22c55e', border: '#166534' }
                          : status === 'failed'
                            ? { bg: 'rgba(239,68,68,0.12)', color: '#f87171', border: '#991b1b' }
                            : { bg: 'rgba(245,158,11,0.12)', color: '#fbbf24', border: '#b45309' };
                      return (
                        <div
                          key={ww.id}
                          style={{
                            padding: '12px 14px',
                            borderRadius: 8,
                            background: 'var(--input-bg, #0f172a)',
                            border: '1px solid var(--border-color, #334155)',
                          }}
                        >
                          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)' }}>{title}</span>
                            <span
                              style={{
                                fontSize: 11,
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                padding: '2px 8px',
                                borderRadius: 6,
                                background: statusStyle.bg,
                                color: statusStyle.color,
                                border: `1px solid ${statusStyle.border}`,
                              }}
                            >
                              {ww.status || '—'}
                            </span>
                          </div>
                          <div style={{ fontSize: 12, marginBottom: 4 }}>
                            <a
                              href={ww.source_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: 'var(--accent, #60a5fa)', wordBreak: 'break-all' }}
                            >
                              {ww.source_url}
                            </a>
                          </div>
                          {ww.user_description ? (
                            <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '6px 0 0', lineHeight: 1.45 }}>
                              <em>User note:</em> {ww.user_description}
                            </p>
                          ) : null}
                          {ww.summary ? (
                            <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '8px 0 0', lineHeight: 1.45 }}>
                              {ww.summary.length > 320 ? `${ww.summary.slice(0, 320)}…` : ww.summary}
                            </p>
                          ) : null}
                          {ww.error_message ? (
                            <p style={{ fontSize: 12, color: '#f87171', margin: '8px 0 0' }}>{ww.error_message}</p>
                          ) : null}
                          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 8 }}>
                            Updated {ww.updated_at ? new Date(ww.updated_at).toLocaleString() : '—'}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <div className="form-group" style={{ margin: '20px 0', padding: '20px', backgroundColor: '#2d3748', border: '2px solid #4a5568', borderRadius: '8px' }}>
              <label style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '12px', display: 'block', color: '#e2e8f0' }}>🔐 Account access</label>
              {isNewUser ? (
                <p style={{ margin: 0, fontSize: '13px', color: '#cbd5e1', lineHeight: 1.6 }}>
                  No password is set here. After you create the account you can email{' '}
                  <code>{editedUser.email || 'the user'}</code> a link to choose their own password.
                </p>
              ) : (
                <>
                  <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#cbd5e1', lineHeight: 1.6 }}>
                    {editedUser.welcomeEmailSentAt
                      ? <>Invite last sent {new Date(editedUser.welcomeEmailSentAt).toLocaleString()}. </>
                      : <>No invite has been sent yet. </>}
                    Sending emails a link that works once and expires in 72 hours, plus the PDF manuals.
                    Any earlier link stops working. Their current password keeps working until they use the new link.
                  </p>
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={() => sendInvite(editedUser.id || user?.id)}
                    disabled={inviteSending || !editedUser.email}
                  >
                    {inviteSending ? 'Sending…' : (editedUser.welcomeEmailSentAt ? 'Resend invite' : 'Send invite')}
                  </button>
                  {renderInviteNotice()}
                </>
              )}
            </div>
            
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
                Cancel
              </button>
              <button 
                type="submit" 
                className="btn btn-primary" 
                disabled={loading || fetchingData}
                aria-busy={loading ? 'true' : 'false'}
                style={loading ? {
                  opacity: 1,
                  minWidth: 120,
                  cursor: 'wait',
                  backgroundColor: 'var(--primary-color)',
                  color: '#fff',
                } : undefined}
              >
                {loading ? 'Saving…' : (isNewUser ? 'Create user' : 'Save User')}
              </button>
            </div>
          </form>
          )}
        </div>
      </div>

      {/* Building Selector Modal */}
      {showBuildingSelector && (
        <div className="modal-overlay" style={{ zIndex: 10002 }}>
          <div className="modal-content" style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>Select Building</h3>
              <button className="close-button" onClick={() => setShowBuildingSelector(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="buildingSearch">Search Buildings</label>
                <input
                  type="text"
                  id="buildingSearch"
                  placeholder="Search by building name or address..."
                  value={buildingSearchQuery}
                  onChange={(e) => setBuildingSearchQuery(e.target.value)}
                  style={{ marginBottom: '16px' }}
                />
              </div>
              <div style={{ 
                maxHeight: '400px', 
                overflowY: 'auto',
                border: '1px solid var(--border-color)',
                borderRadius: '8px'
              }}>
                {filteredBuildings.filter(building => 
                  !editedUser.buildingRoles.some(br => br.buildingId === building.id)
                ).map(building => (
                  <div
                    key={building.id}
                    className="building-option"
                    onClick={() => selectBuilding(building.id)}
                    style={{
                      padding: '12px 16px',
                      cursor: 'pointer',
                      borderBottom: '1px solid var(--border-color)',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--hover-color)'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    <div style={{ fontWeight: 'bold' }}>{building.name}</div>
                    <div style={{ fontSize: '0.9em', color: 'var(--text-secondary)' }}>
                      {building.address}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* BETA user communications timeline */}
      {showUserCommsModal && (
        <div className="modal-overlay" style={{ zIndex: 10005 }}>
          <div className="modal-content" style={{ maxWidth: 820, width: '94vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header">
              <h3 style={{ margin: 0 }}>
                <i className="fas fa-envelope" style={{ marginRight: 8, color: 'var(--accent, #19EFFF)' }}></i>
                Communications — {editedUser.firstName || user?.firstName || ''} {editedUser.lastName || user?.lastName || ''}
              </h3>
              <button
                type="button"
                className="close-button"
                onClick={() => {
                  setShowUserCommsModal(false);
                  setUserCommsSelected(null);
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body" style={{ overflow: 'auto', flex: 1 }}>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 0 }}>
                Account email: <strong style={{ color: 'var(--text-primary)' }}>{editedUser.email || user?.email}</strong>
                <span style={{ marginLeft: 8, fontSize: 11, fontStyle: 'italic', color: 'var(--text-secondary)' }}>
                  (this is Jose&apos;s canonical email on file — each row below shows the actual delivery address for that specific send)
                </span>
                {' · '}
                <button
                  type="button"
                  onClick={() => void loadUserCommunications()}
                  style={{ background: 'none', border: 'none', color: 'var(--accent, #60a5fa)', cursor: 'pointer', padding: 0, fontSize: 13 }}
                >
                  Refresh
                </button>
              </p>
              {userCommsLoading && (
                <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)' }}>
                  <i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }} /> Loading…
                </div>
              )}
              {userCommsError && (
                <div className="error-message" style={{ marginBottom: 12 }}>{userCommsError}</div>
              )}
              {!userCommsLoading && !userCommsError && userCommsItems.length === 0 && (
                <p style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                  No outbound communications recorded for this user yet.
                </p>
              )}
              {!userCommsLoading && userCommsItems.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {userCommsItems.map((item) => {
                    const tone = userCommsKindTone(item.kind);
                    const active = userCommsSelected?.id === item.id;
                    return (
                      <div key={item.id} style={{ display: 'flex', flexDirection: 'column' }}>
                      <button
                        type="button"
                        onClick={() => setUserCommsSelected(active ? null : item)}
                        style={{
                          textAlign: 'left',
                          cursor: 'pointer',
                          padding: '12px 14px',
                          borderRadius: active ? '8px 8px 0 0' : 8,
                          background: active ? 'rgba(25,239,255,0.08)' : 'var(--input-bg, #0f172a)',
                          border: `1px solid ${active ? 'rgba(25,239,255,0.45)' : 'var(--border-color, #334155)'}`,
                          color: 'inherit',
                        }}
                      >
                        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              letterSpacing: '0.04em',
                              padding: '2px 8px',
                              borderRadius: 6,
                              background: tone.bg,
                              color: tone.color,
                              border: `1px solid ${tone.border}`,
                            }}
                          >
                            {userCommsKindLabel(item.kind)}
                          </span>
                          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                            {item.sentAt ? new Date(item.sentAt).toLocaleString() : '—'}
                          </span>
                          {item.meta?.feedbackId != null && (
                            <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                              Feedback #{item.meta.feedbackId}
                            </span>
                          )}
                        </div>
                        <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)', marginBottom: 4 }}>
                          {item.subject}
                        </div>
                        {item.toEmail && (
                          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>
                            Actually delivered to: <strong style={{
                              color: (String(item.toEmail).toLowerCase() === String(editedUser.email || user?.email || '').toLowerCase())
                                ? 'var(--text-primary)'
                                : '#f59e0b'
                            }}>{item.toEmail}</strong>
                            {String(item.toEmail).toLowerCase() !== String(editedUser.email || user?.email || '').toLowerCase() && (
                              <span style={{ marginLeft: 6, fontStyle: 'italic' }}>(differs from account email — mock / test / re-routed send)</span>
                            )}
                          </div>
                        )}
                        {!active && item.bodyPreview && (
                          <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.45 }}>
                            {item.bodyPreview}
                          </div>
                        )}
                      </button>
                      {active && (
                        <div
                          style={{
                            marginTop: -2,
                            padding: 12,
                            borderRadius: '0 0 8px 8px',
                            background: 'rgba(0,0,0,0.35)',
                            border: '1px solid rgba(25,239,255,0.45)',
                            borderTop: 'none',
                            fontSize: 13,
                            lineHeight: 1.55,
                            color: 'var(--text-primary)',
                          }}
                        >
                          {(() => {
                            const raw = String(item.body || '').trim();
                            const looksHtml =
                              raw.startsWith('<') ||
                              /<\/(p|div|br|h[1-6]|ul|ol|li|table)>/i.test(raw) ||
                              /<(img|a|span|table)\b/i.test(raw);
                            if (!raw) {
                              return <span style={{ color: 'var(--text-secondary)' }}>(no body stored)</span>;
                            }
                            if (looksHtml) {
                              return (
                                <iframe
                                  title={`Email preview — ${item.subject || 'message'}`}
                                  srcDoc={raw}
                                  sandbox=""
                                  style={{
                                    width: '100%',
                                    height: 320,
                                    border: '1px solid var(--border-color, #334155)',
                                    borderRadius: 8,
                                    background: '#0d1117',
                                  }}
                                />
                              );
                            }
                            return (
                              <pre
                                style={{
                                  margin: 0,
                                  whiteSpace: 'pre-wrap',
                                  fontFamily: 'inherit',
                                  fontSize: 13,
                                  maxHeight: 280,
                                  overflow: 'auto',
                                }}
                              >
                                {raw}
                              </pre>
                            );
                          })()}
                          {item.meta?.feedbackExcerpt && (
                            <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--border-color)', fontSize: 12, color: 'var(--text-secondary)' }}>
                              <strong>In reply to feedback:</strong> {item.meta.feedbackExcerpt}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <a
                href={`/communications?to=${encodeURIComponent(editedUser.email || user?.email || '')}`}
                className="btn btn-secondary"
                style={{ textDecoration: 'none' }}
              >
                Open Communications page
              </a>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  setShowUserCommsModal(false);
                  setUserCommsSelected(null);
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Role Assignment Modal */}
      {showRoleModal && (
        <div className="modal-overlay" style={{ zIndex: 10001 }}>
          <div className="modal-content" style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>Assign Roles - {getBuildingName(editingBuildingId)}</h3>
              <button className="close-button" onClick={cancelRoleModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
                Select the roles for this user in {getBuildingName(editingBuildingId)}:
              </p>
              <div className="roles-grid" style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', 
                gap: '12px' 
              }}>
                {availableRoles.length === 0 && (
                  <div style={{ padding: '20px', color: 'red', fontWeight: 'bold' }}>
                    No roles available! Check API connection.
                  </div>
                )}
                {availableRoles.map(role => (
                  <label key={role.id} className="role-checkbox" style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    backgroundColor: tempRoles.includes(role.id) ? 'var(--primary-light)' : 'transparent'
                  }}>
                    <input
                      type="checkbox"
                      checked={tempRoles.includes(role.id)}
                      onChange={() => toggleTempRole(role.id)}
                    />
                    <span>{role.name}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={cancelRoleModal}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={saveBuildingRoles}>
                Save Roles
              </button>
            </div>
          </div>
        </div>
      )}

      {showOptiStepUpModal && (
        <div className="modal-overlay" style={{ zIndex: 10003 }}>
          <div className="modal-content" style={{ maxWidth: '440px' }}>
            <div className="modal-header">
              <h3>Confirm OPTI balance change</h3>
              <button type="button" className="close-button" onClick={closeOptiStepUpModal} aria-label="Close">
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
                Your admin dashboard session counts as the first factor. Confirm this change with your{' '}
                <strong>master admin password</strong> and <strong>six-digit authenticator code</strong> when MFA is enabled.
                The update is logged in the token transaction history and security audit trail.
              </p>
              <div className="form-group">
                <label htmlFor="optiNewBal">New OPTI balance</label>
                <input
                  id="optiNewBal"
                  type="number"
                  min="0"
                  step="0.0001"
                  value={optiStepUpNewBalance}
                  onChange={(e) => setOptiStepUpNewBalance(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="optiAdminPw">Admin password</label>
                <input
                  id="optiAdminPw"
                  type="password"
                  autoComplete="current-password"
                  value={optiStepUpPassword}
                  onChange={(e) => setOptiStepUpPassword(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="optiMfa">Authenticator code (6 digits)</label>
                <input
                  id="optiMfa"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="Required if MFA is on"
                  value={optiStepUpMfa}
                  onChange={(e) => setOptiStepUpMfa(e.target.value.replace(/\D/g, '').slice(0, 6))}
                />
              </div>
              <div className="form-group">
                <label htmlFor="optiReason">Reason (optional)</label>
                <input
                  id="optiReason"
                  type="text"
                  maxLength={500}
                  value={optiStepUpReason}
                  onChange={(e) => setOptiStepUpReason(e.target.value)}
                />
              </div>
              {optiStepUpError ? (
                <div style={{ color: '#b91c1c', fontSize: '14px', marginBottom: '12px' }}>{optiStepUpError}</div>
              ) : null}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={closeOptiStepUpModal} disabled={optiStepUpLoading}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={submitOptiStepUp} disabled={optiStepUpLoading}>
                {optiStepUpLoading ? 'Saving…' : 'Apply balance'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default UserModal;
